import { ApiError } from './client';
import type { UploadProgress } from '@/lib/uploadLimits';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export interface XhrUploadOpts {
  /** Wołane w trakcie wysyłki z postępem (loaded / total / percent 0–100). */
  onProgress?: (p: UploadProgress) => void;
  /** Przerwanie wysyłki — jak w `fetch`, po abort promise odrzuca się z `AbortError`. */
  signal?: AbortSignal;
  /** Metoda HTTP (domyślnie POST). */
  method?: string;
}

/**
 * Multipart upload przez XMLHttpRequest — jedyny sposób na `onProgress` wysyłki,
 * bo `fetch` nie raportuje postępu uploadu.
 *
 * Zachowuje semantykę błędów warstwy `api.*`: przy statusie != 2xx rzuca
 * {@link ApiError} z komunikatem z pola `message` odpowiedzi serwera; przerwanie
 * przez `signal` rzuca `DOMException('AbortError')` (zgodnie z `fetch`).
 * Odpowiedzi bez treści / nie-JSON zwracają `undefined` (analogicznie do `api.request`).
 */
export function xhrUpload<T>(path: string, body: FormData, opts?: XhrUploadOpts): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(opts?.method ?? 'POST', `${API_BASE}${path}`);
    xhr.withCredentials = true;

    if (opts?.signal) {
      if (opts.signal.aborted) { reject(new DOMException('Aborted', 'AbortError')); return; }
      opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    if (opts?.onProgress && xhr.upload) {
      xhr.upload.onprogress = ev => {
        if (ev.lengthComputable) {
          opts.onProgress!({
            loaded: ev.loaded,
            total: ev.total,
            percent: Math.round((ev.loaded / ev.total) * 100),
          });
        }
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const txt = xhr.responseText;
        const ct = xhr.getResponseHeader('content-type') ?? '';
        if (!txt || xhr.status === 204 || !ct.includes('application/json')) {
          resolve(undefined as T);
          return;
        }
        try { resolve(JSON.parse(txt) as T); }
        catch { reject(new ApiError(xhr.status, 'Niepoprawna odpowiedź serwera')); }
      } else {
        let msg = xhr.statusText || 'Błąd serwera';
        let payload: unknown;
        try {
          payload = JSON.parse(xhr.responseText);
          msg = (payload as { message?: string }).message ?? msg;
        } catch { /* nie-JSON (np. 413 z nginx) — zostaw statusText */ }
        reject(new ApiError(xhr.status, msg, payload));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, 'Nie udało się połączyć — sprawdź połączenie z siecią'));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    xhr.send(body);
  });
}
